<script lang="ts" setup>
import { CommonBasicImage } from '#components'
import {
  DrawerContent,
  DrawerOverlay,
  DrawerTitle,
  DrawerPortal,
  DrawerRoot,
  DrawerTrigger,
  DrawerClose,
} from 'vaul-vue'
const _props = defineProps<{
  title?: string
  text?: string
  tag?: string
  media?: MediaProps
}>()

const isOpen = ref(false)
const handleUpdate = (open: boolean) => {
  // console.log('handleUpdate', open)
  if (open) {
    isOpen.value = true
    useEvent('should-stop-all-videos', true)
  } else {
    isOpen.value = false
    useEvent('should-play-all-videos', true)
  }
}

// Time formatting helper
function fancyTimeFormat(duration?: number) {
  if (!duration) duration = 0
  const mins = ~~((duration % 3600) / 60)
  const secs = ~~duration % 60
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`
}

const videoDuration = ref(_props.media?.video?.asset?.duration || 0)
const videoPlaybackId = ref(_props.media?.video?.asset?.playbackId)
const videoThumbnailTime = ref(_props.media?.video?.asset?.thumbTime || 0)

const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})
</script>

<template>
  <div>
    <DrawerRoot v-if="media" should-scale-background @update:open="handleUpdate">
      <DrawerTrigger class="media-card group flex h-full w-full cursor-pointer flex-col gap-7.5">
        <div class="media-wrapper relative aspect-[16/9] h-auto w-full overflow-clip rounded-sm">
          <div
            v-if="media.video"
            class="play-button absolute right-5 bottom-5 z-[2] inline-flex h-fit w-fit items-center gap-2 rounded-full border border-white/30 bg-black/30 px-3 py-1 text-white backdrop-blur-xs"
          >
            <IconsPlay class="size-4" />
            <p class="text-sm">{{ fancyTimeFormat(videoDuration) }}</p>
          </div>
          <div class="media-wrapper aspect-[16/9] h-auto w-full overflow-clip text-left">
            <CommonBasicImage
              v-if="media?.image"
              :image="media.image"
              :aspect-mobile="9 / 16"
              :aspect-desktop="9 / 16"
              class="h-full w-full transition-[scale] duration-300 lg:group-hover:scale-108"
              :sizes="'xs:100vw sm:100vw md:100vw lg:70vw xl:70vw xxl:70vw'"
            />
            <nuxt-img
              v-else-if="videoPlaybackId"
              class="h-full w-full transition-[scale] duration-300 lg:group-hover:scale-108"
              :src="`https://image.mux.com/${videoPlaybackId}/thumbnail.png?width=${isMobile ? 400 : 800}&height=${isMobile ? 200 : 400}&fit_mode=smartcrop&time=${videoThumbnailTime}`"
              :sizes="'xs:45vw sm:45vw md:45vw lg:35vw xl:35vw xxl:35vw'"
            />
          </div>
        </div>
        <ElementsTag v-if="tag" v-bind="{ title: tag, outline: true }" />
        <!-- <span
          v-if="tag"
          class="flex w-fit items-center rounded-full border border-black/20 px-3 py-1 text-xs uppercase"
          >{{ tag }}</span
        > -->
        <div class="content lg:w-cols-4 grid w-full gap-(--grid-gap) text-left">
          <h3 v-if="title" class="font-display text-xl">{{ title }}</h3>
          <p v-if="text" class="line-clamp-2 text-base">{{ text }}</p>
        </div>
      </DrawerTrigger>
      <DrawerPortal>
        <DrawerOverlay class="fixed inset-0 z-900 bg-black/60 backdrop-blur-md" />

        <DrawerContent
          class="px-bleed fixed bottom-0 left-0 z-1000 h-fit w-screen rounded-t-sm bg-[#28492C] text-white outline-none"
        >
          <DrawerTitle class="flex items-center justify-between py-4">
            <div class="flex w-full cursor-grab justify-center lg:hidden">
              <div class="h-1 w-8 rounded-full bg-white/30" />
            </div>
            <DrawerClose
              aria-label="Close overlay"
              class="ml-auto hidden cursor-pointer items-center gap-2 text-sm transition-opacity hover:opacity-70 lg:flex"
            >
              <span class="font-display text-sm">{{ $t('media.drawer') }}</span>
              <svg
                width="11"
                height="11"
                viewBox="0 0 11 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line x1="9.51176" y1="1.35355" x2="1.35283" y2="9.51248" stroke="currentColor" />
                <line x1="9.34762" y1="9.51273" x2="1.18869" y2="1.35381" stroke="currentColor" />
              </svg>
            </DrawerClose>
          </DrawerTitle>

          <div
            class="grid max-h-[calc(100dvh-4rem)] gap-10 overflow-auto overscroll-contain pt-0 pb-10"
            data-vaul-no-drag
          >
            <CommonMuxVideoWrapper
              v-if="media?.video"
              class="m-auto aspect-video h-auto w-full overflow-clip rounded-sm"
              :attr="{ controls: true, autoplay: true }"
              :video="media.video"
            />
            <CommonBasicImage
              v-else
              class="m-auto aspect-video h-auto w-full overflow-clip rounded-sm"
              :image="media?.image"
              :aspect-mobile="9 / 16"
              :aspect-desktop="9 / 16"
            />

            <div class="grid gap-10">
              <h2 v-if="title" class="font-display text-xl">{{ title }}</h2>

              <div class="ml-auto grid gap-10 lg:w-1/2">
                <p class="text-base">{{ text }}</p>
              </div>
            </div>
          </div>
        </DrawerContent>
      </DrawerPortal>
    </DrawerRoot>
  </div>
</template>
